import { useEffect, useState } from "react";

import {
  IS_STACKED_BAR_CHART,
  IS_STACKED_BAR_HORIZONTAL_CHART
} from "constants/chart.constants";

import { DataEnum } from "models";

import {
  AXIS_TYPE,
  IS_BOX_PLOT_CHART,
  IS_CROSS_PLOT_CHART,
  IS_TREND_DATE_CHART
} from "../../../constants";
import { EvaChart } from "../../../constants/charts.enums";
import { ChartAxisType } from "../../../models/chart";

/**
 * A custom hook that determines the allowed data types for chart axes group by based on chart type.
 * This is used to filter out specific data types in the group by that are not applicable for
 * the selected chart type.
 *
 * @param {ChartAxisType} axisType - The axis type, either "x" or "y".
 * @param {EvaChart} chartType - The type of chart being rendered.
 * @returns {string[]} An array of allowed data types for the specified axis.
 */
const useAxisDataTypeFilter = (axisType: ChartAxisType, chartType: EvaChart) => {
  const [x, setX] = useState([]);
  const [y, setY] = useState([]);

  const isProbit = chartType === EvaChart.Probit;
  const isBoxPlot = IS_BOX_PLOT_CHART(chartType);
  const isCrossPlot = IS_CROSS_PLOT_CHART(chartType);
  const isStackedBar = IS_STACKED_BAR_CHART(chartType);
  // The horizontal chart's numeric field lives on x, not y — the opposite of
  // StackedBar, so this gets its own flag rather than folding into isStackedBar.
  const isStackedBarHorizontal = IS_STACKED_BAR_HORIZONTAL_CHART(chartType);
  const isDateTrend = IS_TREND_DATE_CHART(chartType);

  useEffect(() => {
    axisType === "x"
      ? setX([
          ...(isCrossPlot ? [DataEnum.Number, DataEnum.Integer, DataEnum.Date] : []),
          ...(isProbit ? [DataEnum.Number, DataEnum.Integer] : []),
          ...(isDateTrend ? [DataEnum.Date, DataEnum.Integer] : []),
          ...(isStackedBarHorizontal ? [DataEnum.Number, DataEnum.Integer] : [])
        ])
      : setY([
          ...(isCrossPlot || isBoxPlot || isStackedBar || isDateTrend
            ? [DataEnum.Number, DataEnum.Integer]
            : [])
        ]);
  }, [
    axisType,
    chartType,
    isBoxPlot,
    isCrossPlot,
    isStackedBar,
    isStackedBarHorizontal,
    isProbit,
    isDateTrend
  ]);

  return axisType === AXIS_TYPE.x ? x : y;
};

export default useAxisDataTypeFilter;
